import { isSome, Maybe } from '@crocodille/common';

export const ACCOUNT_SETTINGS = '/account/settings';
export const ACCOUNT_REGISTRATION_COMPLETE = '/account/registration-complete';
export const ACCOUNT_ORDER_HISTORY = '/account/order-history';
export const ACCOUNT_LOGIN = '/account/login';
export const ACCOUNT_REGISTER = '/account/register';
export const ACCOUNT_ORDER_HISTORY_DETAIL = '/account/order-history-detail';
export const ACCOUNT_PASSWORD_RESET = '/account/password-reset';
export const ACCOUNT_ADDRESS = '/account/address';
export const ACCOUNT_BILLING_ADDRESS = '/account/billing-address';
export const ACCOUNT_LOYALTY_CLUB = '/account/loyalty-club';
export const ACCOUNT_STUDENT_CARD = '/account/student-card';

export function urlEncode(params: Record<string, string | number | boolean>) {
  return Object.entries(params)
    .map(([key, value]) => [key, encodeURIComponent(value)])
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

export function accountLogin(callbackUrl?: string) {
  return (
    ACCOUNT_LOGIN +
    (callbackUrl ? `?callbackUrl=${encodeURIComponent(callbackUrl)}` : '')
  );
}

export function isAccountLoginRoute(
  route: string,
): route is typeof ACCOUNT_LOGIN {
  return route === ACCOUNT_LOGIN;
}

export function isAccountRoute(route: string): route is `/account/${string}` {
  return route.startsWith('/account');
}

export interface OrderHistoryPageParams {
  readonly order?: string | null;
}

export function accountOrderHistoryDetail({
  order,
}: OrderHistoryPageParams = {}) {
  return `/account/order-history-detail${isSome(order) ? `/${order}` : ''}`;
}

export type ShippingType = 'in-restaurant' | 'courier' | 'train-station';

export interface ShippingRouteParams<S extends ShippingType = ShippingType> {
  readonly shipping: S;
  readonly continueTo?: string;
}

export function shippingRoute<S extends ShippingRouteParams['shipping']>({
  shipping,
  continueTo,
}: ShippingRouteParams<S>) {
  if (continueTo) {
    return `/shipping/${shipping}?continueTo=${encodeURIComponent(
      continueTo,
    )}` as const;
  }

  return `/shipping/${shipping}` as const;
}

export function isShippingRoute(route: string) {
  return route === '/shipping/[shipping]';
}

export interface ProductPageServerParams {
  readonly branch: string;
}

export interface ProductsPageParams {
  /**
   * Taxon slug
   */
  readonly taxon?: string;
  /**
   * Product slug
   */
  readonly product?: string;
}

export interface ConfiguratorPageParams {
  /**
   * Taxon slug
   */
  readonly taxon: string;
  /**
   * Product slug
   */
  readonly slug: string;
}

export function products({ taxon, product }: ProductsPageParams = {}) {
  return (
    `/menu${isSome(taxon) ? `/${taxon}` : ''}` +
    (isSome(product) ? `/${encodeURIComponent(product)}` : '')
  );
}

export function configurator({ taxon, slug }: ConfiguratorPageParams) {
  return `/menu/${taxon}/${slug}/configurator`;
}

export function isProductsRoute(
  route: string,
): route is `/menu/[branch]/[taxon]` {
  return route === '/menu/[branch]/[taxon]';
}

export interface ProductPageParams {
  /**
   * Product slug
   */
  readonly product: string;
}

export function product({ product }: ProductPageParams) {
  return `/products/${product}`;
}

export interface MenuRouteServerParams {
  readonly branch: string;
}

export function menu() {
  return `/menu`;
}

export function isMenuRoute(route: string): route is `/menu/[branch]` {
  return route === '/menu/[branch]';
}

export interface CmsPageParams {
  readonly page?: string;
}

export function cmsPages({ page }: CmsPageParams = {}) {
  return `/page${isSome(page) ? `/${page}` : ''}`;
}

export interface BranchPageParams {
  readonly code: string;
}

export function branch({ code }: BranchPageParams): string {
  return `/branch/${code}`;
}

export function checkoutProcess(): string {
  return `/checkout/process`;
}

export function isCheckoutProcessRoute(route: string): boolean {
  return route === `/checkout/process`;
}

export interface OrderThankYouPageParams {
  readonly tokenValue: string;
}

export function orderThankYou({ tokenValue }: OrderThankYouPageParams) {
  return `/order/${tokenValue}/thank-you` as const;
}

export interface CheckoutPaymentPageParams {
  readonly tokenValue: string;
}

export function checkoutPayment({ tokenValue }: CheckoutPaymentPageParams) {
  return `/order/${tokenValue}/payment`;
}

export function isCheckoutPaymentRoute(route: string): boolean {
  return route === `/order/[tokenValue]/payment`;
}

export function isOrderRoute(route: string): boolean {
  return route.startsWith('/order/[tokenValue]');
}

export function orderTracking({ tokenValue }: { readonly tokenValue: string }) {
  return `/order/${tokenValue}/tracking`;
}

export function localized(locale: Maybe<string>, route: string): string {
  if (isSome(locale)) {
    return `/${locale}${route}`;
  } else {
    return route;
  }
}

export function imageProxyUrl({
  q,
  ...params
}: {
  readonly url: string;
  readonly w: number;
  readonly q?: number;
}) {
  return `/_next/image?${urlEncode({ q: q ?? 75, ...params })}`;
}

export function couponDetail({ id }: { id: number }) {
  return `/coupons/${id}`;
}

export function couponList() {
  return `/coupons`;
}

export function downloadInvoice({
  tokenValue,
}: {
  readonly tokenValue: string;
}) {
  return `/api/v2/shop/orders/${tokenValue}/download-invoice`;
}
